import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import * as InstitutionConfidentialityDeedActions from "../../../actions/institutionConfidentialityDeed";
import * as InstitutionContractActions from "../../../actions/institutionContract";
import * as NavigationActions from "../../../actions/navigation";
import {
    InlineLoading,
    Modal,
    NotificationAlert,
} from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    SelectFormInput,
    TextFormInput,
} from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { ModalSize } from "../../../enumerations/ModalSize";
import { RequestState } from "../../../enumerations/RequestState";
import { isUrl } from "../../../functions/string";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionContractBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../../helpers/date";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { IInstitutionConfidentialityDeedReduxFormState } from "../../../reducers/reactReduxForms/institutionConfidentialityDeed";
import { IInstitutionContractReduxFormState } from "../../../reducers/reactReduxForms/institutionContract";

import "./InstitutionContractPage.scss";

interface InstitutionContractPageProps {
    // Institution Contract
    form: Dtos.InstitutionContract & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IInstitutionContractReduxFormState;
    institutionContract: Dtos.InstitutionContract;
    lookups: Dtos.Lookup[];
    loadingInstitutionContract: boolean;
    loadInstitutionContractSuccess: boolean;
    loadInstitutionContractFailure: boolean;
    loadInstitutionContractErrorCode: string;
    savingInstitutionContract: boolean;
    saveInstitutionContractSuccess: boolean;
    saveInstitutionContractFailure: boolean;
    validatingInstitutionContract: boolean;
    validationFailures: Dtos.ResponseError[];
    clearInstitutionContract: InstitutionContractActions.IInstitutionContractClearActionCreator;
    loadInstitutionContract: InstitutionContractActions.IInstitutionContractLoadByInstitutionIdActionCreator;
    saveInstitutionContract: InstitutionContractActions.IInstitutionContractSaveActionCreator;
    validateInstitutionContract: InstitutionContractActions.IInstitutionContractValidateActionCreator;

    // Institution Confidentiality Deed
    institutionConfidentialityDeedForm: Dtos.InstitutionConfidentialityDeed & IRegistrationFormState;
    institutionConfidentialityDeedFormState: Dtos.FormState;
    institutionConfidentialityDeedFormProperties: Dtos.FormProperty[];
    institutionConfidentialityDeedReduxFormState: IInstitutionConfidentialityDeedReduxFormState;
    institutionConfidentialityDeed: Dtos.InstitutionConfidentialityDeed;
    institutionConfidentialityDeedLookups: Dtos.Lookup[];
    loadingInstitutionConfidentialityDeed: boolean;
    loadInstitutionConfidentialityDeedSuccess: boolean;
    loadInstitutionConfidentialityDeedFailure: boolean;
    loadInstitutionConfidentialityDeedErrorCode: string;
    savingInstitutionConfidentialityDeed: boolean;
    saveInstitutionConfidentialityDeedSuccess: boolean;
    saveInstitutionConfidentialityDeedFailure: boolean;
    validatingInstitutionConfidentialityDeed: boolean;
    creatingInstitutionConfidentialityDeed: boolean;
    createInstitutionConfidentialityDeedSuccess: boolean;
    createInstitutionConfidentialityDeedFailure: boolean;
    deletingInstitutionConfidentialityDeed: boolean;
    deleteInstitutionConfidentialityDeedSuccess: boolean;
    institutionConfidentialityDeedValidationFailures: Dtos.ResponseError[];
    clearInstitutionConfidentialityDeed: InstitutionConfidentialityDeedActions.IInstitutionConfidentialityDeedClearActionCreator;
    loadInstitutionConfidentialityDeed: InstitutionConfidentialityDeedActions.IInstitutionConfidentialityDeedLoadByIdActionCreator;
    saveInstitutionConfidentialityDeed: InstitutionConfidentialityDeedActions.IInstitutionConfidentialityDeedSaveActionCreator;
    createInstitutionConfidentialityDeed: InstitutionConfidentialityDeedActions.IInstitutionConfidentialityDeedCreateActionCreator;
    deleteInstitutionConfidentialityDeed: InstitutionConfidentialityDeedActions.IInstitutionConfidentialityDeedDeleteActionCreator;
    validateInstitutionConfidentialityDeed: InstitutionConfidentialityDeedActions.IInstitutionConfidentialityDeedValidateActionCreator;

    // Common
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.institutionContract";
const institutionConfidentialityDeedReduxFormName: string = "reduxForms.institutionConfidentialityDeed";

const InstitutionContractPage: FC<InstitutionContractPageProps> = ({
    form,
    formState,
    formProperties,
    institutionContract,
    lookups,
    loadingInstitutionContract,
    loadInstitutionContractFailure,
    loadInstitutionContractErrorCode,
    savingInstitutionContract,
    saveInstitutionContractSuccess,
    saveInstitutionContractFailure,
    validatingInstitutionContract,
    validationFailures,
    clearInstitutionContract,
    loadInstitutionContract,
    saveInstitutionContract,
    validateInstitutionContract,
    
    institutionConfidentialityDeedForm,
    institutionConfidentialityDeedFormState,
    institutionConfidentialityDeedFormProperties,
    institutionConfidentialityDeed,
    institutionConfidentialityDeedLookups,
    loadingInstitutionConfidentialityDeed,
    loadInstitutionConfidentialityDeedFailure,
    loadInstitutionConfidentialityDeedErrorCode,
    savingInstitutionConfidentialityDeed,
    saveInstitutionConfidentialityDeedSuccess,
    saveInstitutionConfidentialityDeedFailure,
    validatingInstitutionConfidentialityDeed,
    creatingInstitutionConfidentialityDeed,
    deletingInstitutionConfidentialityDeed,
    deleteInstitutionConfidentialityDeedSuccess,
    institutionConfidentialityDeedValidationFailures,
    clearInstitutionConfidentialityDeed,
    loadInstitutionConfidentialityDeed,
    saveInstitutionConfidentialityDeed,
    createInstitutionConfidentialityDeed,
    deleteInstitutionConfidentialityDeed,
    validateInstitutionConfidentialityDeed,

    permissions,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [institutionConfidentialityDeedId, setInstitutionConfidentialityDeedId] = useState<number | null>(null);
    const [formInitalisedInstitutionConfidentialityDeed, setFormInitalisedInstitutionConfidentialityDeed] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [hasBeenSubmittedConfidentalityDeed, setHasBeenSubmittedConfidentalityDeed] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionContract = {} as Dtos.InstitutionContract;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionContract }, extraData)
        );
    };

    const loadInstitutionConfidentialityDeedReduxForm = () => {
        let extraData: Dtos.InstitutionConfidentialityDeed = {} as Dtos.InstitutionConfidentialityDeed;

        loadForm(
            institutionConfidentialityDeedReduxFormName,
            Object.assign({ ...institutionConfidentialityDeed }, extraData)
        );
    };


    // Load Institution Contract
    useEffect(() => {
        if (institutionIdNumber && !institutionContract) {
            setBreadcrumbs(InstitutionContractBreadcrumbs(institutionIdNumber));
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );

            loadInstitutionContract(institutionIdNumber);
        } else if (institutionContract && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        institutionIdNumber,
        institutionContract,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadInstitutionContract,
        InstitutionContractBreadcrumbs,
        setFormInitalised,
    ]);

    // Load Institution Confidentiality Deed
    useEffect(() => {
        if (isOpen) {
            if (
                institutionConfidentialityDeedId &&
                !institutionConfidentialityDeed &&
                !loadingInstitutionConfidentialityDeed
            ) {
                loadInstitutionConfidentialityDeed(institutionConfidentialityDeedId);
            } else if (
                !institutionConfidentialityDeedId &&
                !institutionConfidentialityDeed &&
                !creatingInstitutionConfidentialityDeed
            ) {
                createInstitutionConfidentialityDeed(institutionIdNumber);
            } else if (
                institutionConfidentialityDeed &&
                !formInitalisedInstitutionConfidentialityDeed
            ) {
                loadInstitutionConfidentialityDeedReduxForm();
                setFormInitalisedInstitutionConfidentialityDeed(true);
            }
        }
    }, [
        isOpen,
        institutionConfidentialityDeedId,
        institutionConfidentialityDeed,
        formInitalisedInstitutionConfidentialityDeed,
        loadInstitutionConfidentialityDeed,
        setFormInitalisedInstitutionConfidentialityDeed,
        loadInstitutionConfidentialityDeedReduxForm,
        loadingInstitutionConfidentialityDeed,
        institutionIdNumber,
        createInstitutionConfidentialityDeed,
        creatingInstitutionConfidentialityDeed,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionContractFailure) {
            navigate(`/error/${loadInstitutionContractErrorCode}`);
        }
        if (loadInstitutionConfidentialityDeedFailure) {
            navigate(`/error/${loadInstitutionConfidentialityDeedErrorCode}`);
        }
    }, [
        loadInstitutionContractFailure,
        loadInstitutionContractErrorCode,
        loadInstitutionConfidentialityDeedFailure,
        navigate,
    ]);

    // Reload institution contract & confidentiality deed after delete
    useEffect(() => {
        if (
            !deletingInstitutionConfidentialityDeed &&
            deleteInstitutionConfidentialityDeedSuccess
        ) {
            clearInstitutionContract();
            resetForm(reduxFormName);
            setFormInitalised(false);
            clearInstitutionConfidentialityDeed();
            setInstitutionConfidentialityDeedId(null);
            setIsOpen(false);
            resetForm(institutionConfidentialityDeedReduxFormName);
            setFormInitalisedInstitutionConfidentialityDeed(false);
        }
    }, [
        deletingInstitutionConfidentialityDeed,
        deleteInstitutionConfidentialityDeedSuccess,
        loadInstitutionConfidentialityDeed,
        loadInstitutionContract,
        institutionIdNumber,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institutionContract?.institution) {
            setBreadcrumbs(
                InstitutionContractBreadcrumbs(
                    institutionIdNumber,
                    institutionContract.institution?.name
                )
            );
        }
    }, [
        institutionIdNumber,
        institutionContract,
        setBreadcrumbs,
        InstitutionContractBreadcrumbs,
    ]);

    // Reload institution contract after saving
    useEffect(() => {
        if (!savingInstitutionContract && saveInstitutionContractSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution contract saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/institutions`);
            } else if (!institutionIdNumber) {
                navigate(`/institutions/${institutionIdNumber}/contract`);
                clearInstitutionContract();
            }
        }
    }, [
        savingInstitutionContract,
        saveInstitutionContractSuccess,
        institutionIdNumber,
        navigate,
        saveAndReturn,
        clearInstitutionContract,
    ]);

    // Failed saving institution contract
    useEffect(() => {
        if (!savingInstitutionContract && saveInstitutionContractFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingInstitutionContract, saveInstitutionContractSuccess]);

    // Reload institution confidentiality after saving
    useEffect(() => {
        if (
            !savingInstitutionConfidentialityDeed &&
            saveInstitutionConfidentialityDeedSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution confidentiality saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            clearInstitutionContract();
            resetForm(reduxFormName);
            setFormInitalised(false);
            clearInstitutionConfidentialityDeed();
            setInstitutionConfidentialityDeedId(null);
            setIsOpen(false);
            resetForm(institutionConfidentialityDeedReduxFormName);
            setFormInitalisedInstitutionConfidentialityDeed(false);
        }
    }, [
        savingInstitutionConfidentialityDeed,
        saveInstitutionConfidentialityDeedSuccess,
        setIsOpen,
        clearInstitutionContract,
        clearInstitutionConfidentialityDeed,
        setInstitutionConfidentialityDeedId,
        resetForm,
        reduxFormName,
        institutionConfidentialityDeedReduxFormName,
    ]);

    // Failed saving institution confidentiality deed
    useEffect(() => {
        if (
            !savingInstitutionConfidentialityDeed &&
            saveInstitutionConfidentialityDeedFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingInstitutionConfidentialityDeed,
        saveInstitutionConfidentialityDeedFailure,
    ]);

    // Cleanup effect to clear the institution contract and confidentiality deed on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionContract();
            clearInstitutionConfidentialityDeed();
            resetForm(reduxFormName);
            resetForm(institutionConfidentialityDeedReduxFormName);
        };
    }, [
        clearInstitutionContract,
        clearInstitutionConfidentialityDeed,
        resetForm,
        reduxFormName,
        institutionConfidentialityDeedReduxFormName,
    ]);

    // Institution contract submit
    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.InstitutionContract = {} as Dtos.InstitutionContract;

            if (!data.institutionId) {
                changes.institutionId = institutionIdNumber;
            }

            await saveInstitutionContract(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Institution contract submit failed
    const handleOnSubmitFailed = (_data: Dtos.InstitutionContract) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    // Institution confidentiality deed submit
    const handleOnSubmitInstitutionConfidentialityDeed = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmittedConfidentalityDeed(true);
        }

        if (data) {
            let changes: Dtos.InstitutionConfidentialityDeed = {} as Dtos.InstitutionConfidentialityDeed;

            if (!data.institutionId) {
                changes.institutionId = institutionIdNumber;
            }

            await saveInstitutionConfidentialityDeed(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Institution confidentiality deed submit failed
    const handleOnSubmitFailedInstitutionConfidentialityDeed = (_data: Dtos.InstitutionConfidentialityDeed) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate("/institutions");
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.InstitutionContract = {} as Dtos.InstitutionContract;

            if (form && hasBeenSubmitted && !validatingInstitutionContract) {
                await validateInstitutionContract(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidateConfidentialityDeed = debounce(async () => {
            let changes: Dtos.InstitutionConfidentialityDeed = {} as Dtos.InstitutionConfidentialityDeed;

            if (
                institutionConfidentialityDeedForm &&
                hasBeenSubmittedConfidentalityDeed &&
                !validatingInstitutionConfidentialityDeed
            ) {
                await validateInstitutionConfidentialityDeed(
                    Object.assign(
                        { ...institutionConfidentialityDeedForm },
                        changes
                    )
                );
            }
        }, 250);

        debounceValidateConfidentialityDeed();

        return () => {
            debounceValidateConfidentialityDeed.cancel();
        };
    }, [institutionConfidentialityDeedForm]);

    const openLink = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const generateDeleteButton = () => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.InstitutionConfidentialityDeedUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`institutionConfidentialityDeed-DeleteButton-${institutionContract?.institutionConfidentialityDeed?.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setOpenDeleteModal(true);
                }}
            >
                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.TRASH} fixedWidth />
            </button>
        );
    };

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.InstitutionConfidentialityDeedUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openDeleteModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenDeleteModal(false);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenDeleteModal(false);
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the confidentiality
                            deed "
                            <em>
                                {
                                    institutionContract
                                        ?.institutionConfidentialityDeed?.name
                                }
                                "
                            </em>{" "}
                            from institution{" "}
                            <strong>
                                {institutionContract?.institution?.name}
                            </strong>
                            .
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteInstitutionConfidentialityDeed(institutionContract?.institutionConfidentialityDeed?.id);
                                setOpenDeleteModal(false);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenDeleteModal(false);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        openDeleteModal,
        permissions,
        setOpenDeleteModal,
        institutionIdNumber,
        institutionConfidentialityDeed,
        institutionContract,
        deleteInstitutionConfidentialityDeed,
    ]);

    const renderConfidentialityDeedModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [
                Dtos.Permission.InstitutionConfidentialityDeedUpdate,
                Dtos.Permission.InstitutionConfidentialityDeedCreate,
            ]
        );

        if (hasPermission) {
            const formDisabled: boolean =
                !hasPermission ||
                savingInstitutionConfidentialityDeed ||
                (institutionConfidentialityDeedFormState &&
                    institutionConfidentialityDeedFormState.formStatus ==
                        Dtos.FormStatus.Disabled);

            let formComponents: { [index: string]: RegistrationFormComponent } =
                {
                    detailsGroup: {
                        type: RegistrationComponent.RegistrationElement,
                        component: (
                            <>
                                <div className="form-row pt-4 pb-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            institutionConfidentialityDeedForm,
                                            "institutionConfidentialityDeed",
                                            {
                                                name: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                    fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                                },
                                            },
                                            institutionConfidentialityDeedLookups,
                                            institutionConfidentialityDeedValidationFailures,
                                            institutionConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            institutionConfidentialityDeedForm,
                                            "institutionConfidentialityDeed",
                                            {
                                                location: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        addOnRightButton: (
                                                            <button
                                                                className="btn btn-secondary"
                                                                type="button"
                                                                id="button-addon2"
                                                                disabled={institutionConfidentialityDeedForm?.location == null || !isUrl(institutionConfidentialityDeedForm?.location)} 
                                                                style={{
                                                                    cursor: institutionConfidentialityDeedForm?.location == null || !isUrl(institutionConfidentialityDeedForm?.location)
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                    padding: "4px 10px",
                                                                }}
                                                                onClick={() => {
                                                                    openLink(institutionConfidentialityDeedForm.location);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                            </button>
                                                        ),
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                    fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                                },
                                            },
                                            institutionConfidentialityDeedLookups,
                                            institutionConfidentialityDeedValidationFailures,
                                            institutionConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row pt-2 pb-4 px-4">
                                    <div className="col-12 col-xl-6">
                                        {renderFormControls(
                                            institutionConfidentialityDeedForm,
                                            "institutionConfidentialityDeed",
                                            {
                                                dateOfDeed: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: DatePickerFormInputFabric,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                    fieldClassName: "col-sm-12 col-xl-7 generic-form-field",
                                                },
                                            },
                                            institutionConfidentialityDeedLookups,
                                            institutionConfidentialityDeedValidationFailures,
                                            institutionConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                    <div className="col-12 col-xl-6">
                                        {renderFormControls(
                                            institutionConfidentialityDeedForm,
                                            "institutionConfidentialityDeed",
                                            {
                                                dateFullyExecuted: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: DatePickerFormInputFabric,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                    fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                                },
                                            },
                                            institutionConfidentialityDeedLookups,
                                            institutionConfidentialityDeedValidationFailures,
                                            institutionConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                            </>
                        ),
                    },
                };

            return (
                <Modal
                    open={isOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setIsOpen(false);
                        setInstitutionConfidentialityDeedId(null);
                        clearInstitutionConfidentialityDeed();
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div className="modal-title">
                            <h2 className="mb-0">Confidentiality Deed</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setIsOpen(false);
                                setInstitutionConfidentialityDeedId(null);
                                clearInstitutionConfidentialityDeed();
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body" style={{ minHeight: "280px" }}>
                        {loadingInstitutionConfidentialityDeed || creatingInstitutionConfidentialityDeed ? (
                            <div className="d-flex h-100 align-items-center justify-content-center">
                                <InlineLoading />
                            </div>
                        ) : (
                            <Form
                                model={institutionConfidentialityDeedReduxFormName}
                                onSubmit={(val, _event) =>
                                    handleOnSubmitInstitutionConfidentialityDeed(val)
                                }
                                onSubmitFailed={(val) =>
                                    handleOnSubmitFailedInstitutionConfidentialityDeed(val)
                                }
                            >
                                {renderFormControls(
                                    institutionConfidentialityDeedForm,
                                    "institutionConfidentialityDeed",
                                    formComponents,
                                    institutionConfidentialityDeedLookups,
                                    institutionConfidentialityDeedValidationFailures,
                                    institutionConfidentialityDeedFormProperties,
                                    changeForm
                                )}
                                <div className="mt-4 mb-4 text-right">
                                    <button
                                        type="submit"
                                        disabled={formDisabled}
                                        className="btn btn-primary mr-2"
                                        onClick={() => {
                                            changeForm(
                                                institutionConfidentialityDeedReduxFormName + ".saveAndReturn",
                                                false
                                            );
                                        }}
                                    >
                                        Submit
                                    </button>
                                    <button
                                        className="btn btn-primary mr-2"
                                        onClick={() => {
                                            setIsOpen(false);
                                            setInstitutionConfidentialityDeedId(null);
                                            clearInstitutionConfidentialityDeed();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Form>
                        )}
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        isOpen,
        permissions,
        setIsOpen,
        loadingInstitutionConfidentialityDeed,
        institutionConfidentialityDeedReduxFormName,
        handleOnSubmitInstitutionConfidentialityDeed,
        handleOnSubmitFailedInstitutionConfidentialityDeed,
        savingInstitutionConfidentialityDeed,
        institutionConfidentialityDeedFormState,
        institutionConfidentialityDeedForm,
        openLink,
        setInstitutionConfidentialityDeedId,
        clearInstitutionConfidentialityDeed,
        creatingInstitutionConfidentialityDeed,
        changeForm,
        institutionConfidentialityDeedFormProperties,
        institutionConfidentialityDeedValidationFailures,
        institutionConfidentialityDeedLookups,
    ]);

    const renderConfidentialityDeedContent = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [
                Dtos.Permission.InstitutionConfidentialityDeedUpdate,
                Dtos.Permission.InstitutionConfidentialityDeedCreate,
            ]
        );

        if (
            form?.institutionConfidentialityDeed &&
            form?.institutionConfidentialityDeed?.id
        ) {
            return (
                <div className="card card-shadow" style={{ zIndex: 1 }}>
                    <div className="table-wrapper">
                        <ReactTable
                            data={[form.institutionConfidentialityDeed]}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            defaultPageSize={1}
                            pageSize={1}
                            showPagination={false}
                            className="-striped -highlight -clickable"
                            noDataText={`${form?.institution?.name} has no confidentiality deed to view.`}
                            resizable={false}
                            onPageSizeChange={(_val) => {}}
                            getTdProps={(
                                _state,
                                rowInfo: RowInfo,
                                _column,
                                _instance
                            ) => {
                                return {
                                    onClick: (_e, handleOriginal) => {
                                        if (hasPermission) {
                                            setIsOpen(true);
                                            setInstitutionConfidentialityDeedId(rowInfo.original.id);
                                        }

                                        if (handleOriginal) {
                                            handleOriginal();
                                        }
                                    },
                                    style: {
                                        color:
                                            rowInfo &&
                                            rowInfo.original &&
                                            rowInfo.original.active
                                                ? "#212529"
                                                : "#9e9e9e",
                                    },
                                };
                            }}
                            defaultSorted={[
                                {
                                    id: "id",
                                    desc: false,
                                },
                            ]}
                            columns={[
                                {
                                    id: "id",
                                    Header: " ",
                                    accessor: "id",
                                    maxWidth: 50,
                                    Cell: (_props) => (
                                        <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.SIGNATURE} fixedWidth />
                                    ),
                                },
                                {
                                    id: "name",
                                    Header: "Name",
                                    accessor: "name",
                                },
                                {
                                    id: "dateOfDeed",
                                    Header: "Date Of Deed",
                                    accessor: "dateOfDeed",
                                    maxWidth: 250,
                                    Cell: (props) => (
                                        <div>
                                            <span
                                                data-tip={convertToApproximateDateString(props.value)}
                                            >
                                                {convertToShortDateString(props.value)}
                                            </span>
                                            <ReactTooltip />
                                        </div>
                                    ),
                                },
                                {
                                    id: "dateFullyExecuted",
                                    Header: "Date Fully Executed",
                                    accessor: "dateFullyExecuted",
                                    maxWidth: 250,
                                    Cell: (props) => (
                                        <div>
                                            <span
                                                data-tip={convertToApproximateDateString(props.value)}
                                            >
                                                {convertToShortDateString(props.value)}
                                            </span>
                                            <ReactTooltip />
                                        </div>
                                    ),
                                },
                                {
                                    id: "delete",
                                    Header: " ",
                                    maxWidth: 75,
                                    Cell: (_props) => (
                                        <div>{generateDeleteButton()}</div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className="card card-shadow p-3" style={{ zIndex: 1 }}>
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                        </div>
                        <span>
                            <i>{form?.institution?.name}</i> has no confidentiality deed to view.
                        </span>
                    </div>
                    {hasPermission && (
                        <div className="mt-3 d-flex justify-content-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsOpen(true);
                                }}
                                className="btn btn-primary"
                            >
                                Add
                            </button>
                        </div>
                    )}
                </div>
            );
        }
    }, [
        form,
        setIsOpen,
        PAGE_SIZE_OPTIONS,
        convertToApproximateDateString,
        convertToShortDateString,
        setInstitutionConfidentialityDeedId,
        permissions,
    ]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                institutionIdNumber,
                null,
                permissions,
                Dtos.Permission.InstitutionContractUpdate,
                Dtos.Permission.InstitutionContractCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionContract ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "institutionContract",
                                        {
                                            agreementName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "institutionContract",
                                        {
                                            noOfOriginalCTRARequired: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "institutionContract",
                                        {
                                            institutionContractSiteTypeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "InstitutionContract",
                                                                "institutionContractSiteTypeId",
                                                                lookups
                                                            ),
                                                            form.institutionContractSiteTypeId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            confidentialityDeedGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Confidentiality Deed</h1>
                        {renderConfidentialityDeedContent}
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "institutionContract",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        type="submit"
                        className="btn btn-primary mr-2"
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                    >
                        Submit
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary mr-2"
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                    >
                        Submit And Return
                    </button>
                    <button className="btn btn-primary mr-2" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingInstitutionContract,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        institutionContract,
        renderConfidentialityDeedContent,
        institutionIdNumber,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.InstitutionContractView}
            loading={
                loadingInstitutionContract || 
                savingInstitutionContract || 
                loadingInstitutionConfidentialityDeed || 
                savingInstitutionConfidentialityDeed
            }
        >
            {renderContent}
            {renderConfidentialityDeedModal}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionContractPageProps> => {
    let institutionContract: Dtos.InstitutionContract | undefined = !(
        state.institutionContract.formData instanceof Array
    )
        ? state.institutionContract.formData
        : undefined;
    let institutionConfidentialityDeed:
        | Dtos.InstitutionConfidentialityDeed
        | undefined = !(
        state.institutionConfidentialityDeed.formData instanceof Array
    )
        ? state.institutionConfidentialityDeed.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionContract: institutionContract!,
        loadingInstitutionContract: state.institutionContract.loadState && state.institutionContract.loadState.status == RequestState.Pending,
        loadInstitutionContractSuccess: state.institutionContract.loadState && state.institutionContract.loadState.status == RequestState.Success,
        loadInstitutionContractFailure: state.institutionContract.loadState && state.institutionContract.loadState.status == RequestState.Failure,
        loadInstitutionContractErrorCode: state.institutionContract.loadState.errorCode,
        savingInstitutionContract: state.institutionContract.saveState && state.institutionContract.saveState.status == RequestState.Pending,
        saveInstitutionContractSuccess: state.institutionContract.saveState && state.institutionContract.saveState.status == RequestState.Success,
        saveInstitutionContractFailure: state.institutionContract.saveState && state.institutionContract.saveState.status == RequestState.Failure,
        validatingInstitutionContract: state.institutionContract.validateState && state.institutionContract.validateState.status == RequestState.Pending,
        lookups: state.institutionContract.lookups,
        form: state.reduxForms.institutionContract,
        formState: state.institutionContract.formState,
        formProperties: state.institutionContract.formProperties,
        reduxFormState: state.reduxForms.formState.institutionContract,
        validationFailures: institutionContract
            ? state.institutionContract.validationFailures
            : undefined,

        institutionConfidentialityDeed: institutionConfidentialityDeed!,
        loadingInstitutionConfidentialityDeed: state.institutionConfidentialityDeed.loadState && state.institutionConfidentialityDeed.loadState.status == RequestState.Pending,
        loadInstitutionConfidentialityDeedSuccess: state.institutionConfidentialityDeed.loadState && state.institutionConfidentialityDeed.loadState.status == RequestState.Success,
        loadInstitutionConfidentialityDeedFailure: state.institutionConfidentialityDeed.loadState && state.institutionConfidentialityDeed.loadState.status == RequestState.Failure,
        loadInstitutionConfidentialityDeedErrorCode: state.institutionConfidentialityDeed.loadState.errorCode,
        savingInstitutionConfidentialityDeed: state.institutionConfidentialityDeed.saveState && state.institutionConfidentialityDeed.saveState.status == RequestState.Pending,
        saveInstitutionConfidentialityDeedSuccess: state.institutionConfidentialityDeed.saveState && state.institutionConfidentialityDeed.saveState.status == RequestState.Success,
        saveInstitutionConfidentialityDeedFailure: state.institutionConfidentialityDeed.saveState && state.institutionConfidentialityDeed.saveState.status == RequestState.Failure,
        validatingInstitutionConfidentialityDeed: state.institutionConfidentialityDeed.validateState && state.institutionConfidentialityDeed.validateState.status == RequestState.Pending,
        creatingInstitutionConfidentialityDeed: state.institutionConfidentialityDeed.createState && state.institutionConfidentialityDeed.createState.status == RequestState.Pending,
        createInstitutionConfidentialityDeedSuccess: state.institutionConfidentialityDeed.createState && state.institutionConfidentialityDeed.createState.status == RequestState.Success,
        createInstitutionConfidentialityDeedFailure: state.institutionConfidentialityDeed.createState && state.institutionConfidentialityDeed.createState.status == RequestState.Failure,
        deletingInstitutionConfidentialityDeed: state.institutionConfidentialityDeed.deleteState && state.institutionConfidentialityDeed.deleteState.status == RequestState.Pending,
        deleteInstitutionConfidentialityDeedSuccess: state.institutionConfidentialityDeed.deleteState && state.institutionConfidentialityDeed.deleteState.status == RequestState.Success,
        institutionConfidentialityDeedLookups: state.institutionConfidentialityDeed.lookups,
        institutionConfidentialityDeedForm: state.reduxForms.institutionConfidentialityDeed,
        institutionConfidentialityDeedFormState: state.institutionConfidentialityDeed.formState,
        institutionConfidentialityDeedFormProperties: state.institutionConfidentialityDeed.formProperties,
        institutionConfidentialityDeedReduxFormState: state.reduxForms.formState.institutionConfidentialityDeed,
        institutionConfidentialityDeedValidationFailures:
            institutionConfidentialityDeed
                ? state.institutionConfidentialityDeed.validationFailures
                : undefined,

        user: state.user.data,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionContractPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),

        loadInstitutionContract: bindActionCreators(InstitutionContractActions.LoadInstitutionContractByInstitutionId, dispatch),
        clearInstitutionContract: bindActionCreators(InstitutionContractActions.Clear, dispatch),
        saveInstitutionContract: bindActionCreators(InstitutionContractActions.SaveInstitutionContract, dispatch),
        validateInstitutionContract: bindActionCreators(InstitutionContractActions.ValidateInstitutionContract, dispatch),

        loadInstitutionConfidentialityDeed: bindActionCreators(InstitutionConfidentialityDeedActions.LoadInstitutionConfidentialityDeedById, dispatch),
        clearInstitutionConfidentialityDeed: bindActionCreators(InstitutionConfidentialityDeedActions.Clear, dispatch),
        saveInstitutionConfidentialityDeed: bindActionCreators(InstitutionConfidentialityDeedActions.SaveInstitutionConfidentialityDeed, dispatch),
        createInstitutionConfidentialityDeed: bindActionCreators(InstitutionConfidentialityDeedActions.CreateInstitutionConfidentialityDeed, dispatch),
        deleteInstitutionConfidentialityDeed: bindActionCreators(InstitutionConfidentialityDeedActions.DeleteInstitutionConfidentialityDeed, dispatch),
        validateInstitutionConfidentialityDeed: bindActionCreators(InstitutionConfidentialityDeedActions.ValidateInstitutionConfidentialityDeed, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InstitutionContractPage);
